import { fromJS } from 'immutable';
import ACTIONS from '../app.constants';
import {
    ApplicationReducerActionType,
    ApplicationReducer,
    ApplicationList,
    Violation,
    AccountingTransactionList,
    AccountingTransactionDisplayList,
    Inspection,
    Comment,
    InspectionList,
    ViolationList,
    CommentList,
    FileList,
} from '../modules/application/types';

export default function applicationReducer(
    state = fromJS({
        applicationListSearched: {},
        applicationList: { loading: true },
        applicationDetail: {},
        applicationEditForm: {},
        applicationFiles: {},
        applicationHistories: {},
        applicationComment: {},
        licenseAccounting: {},
        licenseFilters: {},
        licenseType: {},
        licenseTypes: {},
        inspectionEditForm: {},
        activeTab: '',
        printDocumentsHistoryList: {},
    }),
    action: ApplicationReducerActionType,
): ApplicationReducer {
    switch (action.type) {
        case ACTIONS.GET_APPLICATIONS_LIST.PENDING: {
            if (action.data?.status) {
                return state
                    .setIn(['applicationList', 'loading'], action.data.status)
                    .setIn(['applicationList', 'filterLoading'], !action.data.status);
            } else {
                return state
                    .setIn(['applicationList', 'loading'], action.data.status)
                    .setIn(['applicationList', 'filterLoading'], !action.data.status);
            }
        }
        case ACTIONS.GET_APPLICATIONS_LIST.SUCCESS: {
            let loadedList = state.getIn(['applicationList', 'data']);
            let licensesCount = state.getIn(['applicationList', 'licensesCount']);
            let loadMore = true;
            let appList: ApplicationList = action.data as ApplicationList;
            if (loadedList && action.pageNumber > 0) {
                loadedList = loadedList.concat(appList);
            } else {
                loadedList = appList;
                licensesCount = action.licensesCount;
            }
            if (!(appList && appList.length > 0)) {
                loadMore = false;
            }
            return state
                .setIn(['applicationList', 'licensesCount'], licensesCount)
                .setIn(['applicationList', 'loadMore'], loadMore)
                .setIn(['applicationList', 'data'], loadedList)
                .setIn(['applicationList', 'loading'], false)
                .setIn(['applicationList', 'filterLoading'], false);
        }
        case ACTIONS.GET_APPLICATIONS_LIST.ERROR: {
            return state
                .setIn(['applicationList', 'loading'], false)
                .setIn(['applicationList', 'filterLoading'], false);
        }
        case ACTIONS.GET_APPLICATIONS_DETAIL.PENDING: {
            return state.setIn(['applicationList', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.GET_APPLICATIONS_DETAIL.SUCCESS: {
            return state
                .setIn(['applicationList', 'loading'], false)
                .setIn(['applicationDetail', 'data'], action.data.license)
                .setIn(['licenseType', 'data'], action.data.licenseType)
                .setIn(['applicationEditForm', 'data'], action.data.editForm);
        }
        case ACTIONS.GET_APPLICATIONS_DETAIL.ERROR: {
            return state.setIn(['applicationList', 'loading'], false);
        }
        case ACTIONS.GET_FILE_LIST.PENDING: {
            return state.setIn(['applicationFiles', 'loading'], action.status === 'LOADING');
        }
        case ACTIONS.GET_FILE_LIST.SUCCESS: {
            let loadedList = state.getIn(['applicationFiles', 'data']) ?? [];

            const FilesList: FileList = action.data as FileList;

            const loadMore = FilesList.length >= (action?.paginationData?.limit ?? Infinity);

            if (loadedList && (action?.paginationData?.skip ?? 0) > 0) {
                loadedList = loadedList.concat(FilesList);
            } else {
                loadedList = FilesList;
            }

            return state
                .setIn(['applicationFiles', 'loading'], false)
                .setIn(['applicationFiles', 'loadMore'], loadMore)
                .setIn(['applicationFiles', 'data'], loadedList)
                .setIn(['applicationFiles', 'error'], false);
        }
        case ACTIONS.GET_FILE_LIST.ERROR: {
            return state
                .setIn(['applicationFiles', 'loading'], false)
                .setIn(['applicationFiles', 'data'], [])
                .setIn(['applicationFiles', 'error'], true);
        }
        case ACTIONS.GET_LICENSE_ACCOUNTING.PENDING: {
            return state.setIn(['licenseAccounting', 'loading'], action.status === 'LOADING');
        }
        case ACTIONS.GET_LICENSE_ACCOUNTING.SUCCESS: {
            let loadedList = state.getIn(['licenseAccounting', 'data']) ?? [];

            const transactionList: AccountingTransactionList = action.data;

            const loadMore = transactionList.length >= (action.paginationData?.limit ?? Infinity);

            const displayList: AccountingTransactionDisplayList = transactionList.map((transaction) => ({
                date: transaction.journalEntry.date,
                description: transaction.description,
                chargeAmount: transaction.type === 'debit' ? transaction.amount : undefined,
                paymentAmount: transaction.type === 'credit' ? transaction.amount : undefined,
                balance: transaction.balance,
            }));

            if (loadedList && action.paginationData?.skip) {
                loadedList = loadedList.concat(displayList);
            } else {
                loadedList = displayList;
            }

            return state
                .setIn(['licenseAccounting', 'loading'], false)
                .setIn(['licenseAccounting', 'loadMore'], loadMore)
                .setIn(['licenseAccounting', 'data'], loadedList)
                .setIn(['licenseAccounting', 'error'], false);
        }
        case ACTIONS.GET_LICENSE_ACCOUNTING.ERROR: {
            return state
                .setIn(['licenseAccounting', 'loading'], false)
                .setIn(['licenseAccounting', 'data'], [])
                .setIn(['licenseAccounting', 'error'], true);
        }

        case ACTIONS.GET_AUDIT_HISTORY.PENDING: {
            return state.setIn(['applicationHistories', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.GET_AUDIT_HISTORY.SUCCESS: {
            const loadedList = state.getIn(['applicationHistories', 'data']) ?? [];
            const hasMore = !(action.data.length < action.paginationData?.limit);
            return state
                .setIn(['applicationHistories', 'loading'], false)
                .setIn(['applicationHistories', 'hasMore'], hasMore)
                .setIn(
                    ['applicationHistories', 'data'],
                    action.paginationData?.skip > 0 ? loadedList.concat(action.data) : action.data,
                );
        }
        case ACTIONS.GET_AUDIT_HISTORY.ERROR: {
            return state.setIn(['applicationHistories', 'loading'], false);
        }

        case ACTIONS.LICENSE_SEARCH.PENDING: {
            return state.setIn(['applicationListSearched', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.LICENSE_SEARCH.SUCCESS: {
            return state
                .setIn(['applicationListSearched', 'loading'], false)
                .setIn(['applicationListSearched', 'data'], action.data);
        }
        case ACTIONS.LICENSE_SEARCH.ERROR: {
            return state
                .setIn(['applicationListSearched', 'loading'], false)
                .setIn(['applicationListSearched', 'error'], action.error);
        }

        case ACTIONS.UPDATE_LICENSE.PENDING: {
            return state.setIn(['applicationData', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.UPDATE_LICENSE.SUCCESS: {
            return state.setIn(['applicationData', 'loading'], false);
        }
        case ACTIONS.UPDATE_LICENSE.ERROR: {
            return state.setIn(['applicationData', 'loading'], false).setIn(['applicationData', 'error'], action.error);
        }

        case ACTIONS.CHANGE_LICENSE_FILTERS: {
            return state.setIn(['licenseFilters', 'listFilters'], action.payload.data);
        }

        case ACTIONS.GET_LICENSE_TYPES.PENDING: {
            return state.setIn(['licenseTypes', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.GET_LICENSE_TYPES.SUCCESS: {
            return state.setIn(['licenseTypes', 'loading'], false).setIn(['licenseTypes', 'data'], action.data);
        }
        case ACTIONS.GET_LICENSE_TYPES.ERROR: {
            return state.setIn(['licenseTypes', 'loading'], false).setIn(['licenseTypes', 'error'], action.error);
        }

        case ACTIONS.GET_VIOLATION_LIST.PENDING: {
            return state.setIn(['applicationViolation', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.GET_VIOLATION_LIST.SUCCESS: {
            let loadedList = state.getIn(['applicationViolation', 'data']) ?? [];
            const violationList: ViolationList = action.data as ViolationList;

            const loadMore = violationList.length < action?.paginationData?.limit ? false : true;
            if (loadedList && action?.paginationData?.skip > 0) {
                loadedList = loadedList.concat(violationList);
            } else {
                loadedList = violationList;
            }
            return state
                .setIn(['applicationViolation', 'loading'], false)
                .setIn(['applicationViolation', 'loadMore'], loadMore)
                .setIn(['applicationViolation', 'data'], loadedList);
        }
        case ACTIONS.GET_VIOLATION_LIST.ERROR: {
            return state.setIn(['applicationViolation', 'loading'], false);
        }

        case ACTIONS.GET_INSPECTION_LIST.PENDING: {
            return state.setIn(['applicationInspection', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.GET_INSPECTION_LIST.SUCCESS: {
            let loadedList = state.getIn(['applicationInspection', 'data']) ?? [];
            const inspectionList: InspectionList = action.data as InspectionList;

            const loadMore = inspectionList.length < action?.paginationData?.limit ? false : true;
            if (loadedList && action?.paginationData?.skip > 0) {
                loadedList = loadedList.concat(inspectionList);
            } else {
                loadedList = inspectionList;
            }
            return state
                .setIn(['applicationInspection', 'loading'], false)
                .setIn(['applicationInspection', 'loadMore'], loadMore)
                .setIn(['applicationInspection', 'data'], loadedList)
                .setIn(['applicationInspection', 'inspectionCount'], action.inspectionCount);
        }
        case ACTIONS.GET_INSPECTION_LIST.ERROR: {
            return state.setIn(['applicationInspection', 'loading'], false);
        }

        case ACTIONS.GET_CATEGORY_LIST.PENDING: {
            return state.setIn(['applicationCategoryList', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.GET_CATEGORY_LIST.SUCCESS: {
            return state
                .setIn(['applicationCategoryList', 'loading'], false)
                .setIn(['applicationCategoryList', 'data'], action.data);
        }
        case ACTIONS.GET_CATEGORY_LIST.ERROR: {
            return state.setIn(['applicationCategoryList', 'loading'], false);
        }

        case ACTIONS.ADD_VIOLATION.PENDING: {
            return state.setIn(['applicationViolation', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.ADD_VIOLATION.SUCCESS: {
            const data = action.data;
            const violation: Violation[] = state.getIn(['applicationViolation', 'data']);

            const newViolationData = violation.concat(data);

            return state
                .setIn(['applicationViolation', 'loading'], false)
                .setIn(['applicationViolation', 'data'], newViolationData);
        }
        case ACTIONS.ADD_VIOLATION.ERROR: {
            return state
                .setIn(['applicationViolation', 'loading'], false)
                .setIn(['applicationViolation', 'error'], action.error);
        }

        case ACTIONS.DELETE_INSPECTION_RECORD.PENDING: {
            return state.setIn(['applicationInspection', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.DELETE_INSPECTION_RECORD.SUCCESS: {
            const inspectionId = action.payload.inspectionId;

            const inspectionList = state.getIn(['applicationInspection', 'data']);
            const filteredInspections = inspectionList.filter(
                (inspection: Inspection) => inspection.id !== inspectionId,
            );
            const inspectionCount = state.getIn(['applicationInspection', 'inspectionCount']);

            return state
                .setIn(['applicationInspection', 'loading'], false)
                .setIn(['applicationInspection', 'data'], filteredInspections)
                .setIn(['applicationInspection', 'inspectionCount'], inspectionCount - 1);
        }
        case ACTIONS.DELETE_INSPECTION_RECORD.ERROR: {
            return state
                .setIn(['applicationInspection', 'loading'], false)
                .setIn(['applicationInspection', 'error'], action.error);
        }
        case ACTIONS.GET_USER_LIST.PENDING: {
            return state.setIn(['addInspectionUserList', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.GET_USER_LIST.SUCCESS: {
            return state
                .setIn(['addInspectionUserList', 'loading'], false)
                .setIn(['addInspectionUserList', 'data'], action.data);
        }
        case ACTIONS.GET_USER_LIST.ERROR: {
            return state.setIn(['addInspectionUserList', 'loading'], false);
        }

        case ACTIONS.GET_INSPECTION_TYPE.PENDING: {
            return state.setIn(['inspectionTypeList', 'loading'], true);
        }
        case ACTIONS.GET_INSPECTION_TYPE.SUCCESS: {
            return state
                .setIn(['inspectionTypeList', 'loading'], false)
                .setIn(['inspectionTypeList', 'data'], action.data);
        }
        case ACTIONS.GET_INSPECTION_TYPE.ERROR: {
            return state.setIn(['inspectionTypeList', 'loading'], false);
        }
        case ACTIONS.ADD_INSPECTION.PENDING: {
            return state.setIn(['applicationInspection', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.ADD_INSPECTION.SUCCESS: {
            const data = action.data;
            const inspection: Inspection[] = state.getIn(['applicationInspection', 'data']);
            const newInspectionData = inspection.concat(data);
            const inspectionCount = state.getIn(['applicationInspection', 'inspectionCount']);
            return state
                .setIn(['applicationInspection', 'loading'], false)
                .setIn(['applicationInspection', 'data'], newInspectionData)
                .setIn(['applicationInspection', 'inspectionCount'], inspectionCount + 1);
        }

        case ACTIONS.ADD_INSPECTION.ERROR: {
            return state
                .setIn(['applicationInspection', 'loading'], false)
                .setIn(['applicationInspection', 'error'], action.error);
        }

        case ACTIONS.DELETE_VIOLATION.PENDING: {
            return state.setIn(['applicationViolation', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.DELETE_VIOLATION.SUCCESS: {
            const violationId = action.payload.violationId;
            const violation = state.getIn(['applicationViolation', 'data']);
            const filteredViolations = violation.filter((violation: Violation) => violation.id !== violationId);
            return state
                .setIn(['applicationViolation', 'loading'], false)
                .setIn(['applicationViolation', 'data'], filteredViolations);
        }
        case ACTIONS.DELETE_VIOLATION.ERROR: {
            return state
                .setIn(['applicationViolation', 'loading'], false)
                .setIn(['applicationViolation', 'error'], action.error);
        }
        case ACTIONS.ADD_COMMENT.ERROR: {
            return state
                .setIn(['applicationComment', 'loading'], false)
                .setIn(['applicationComment', 'error'], action.error);
        }
        case ACTIONS.ADD_COMMENT.PENDING: {
            return state.setIn(['applicationComment', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.ADD_COMMENT.SUCCESS: {
            const data = action.data;
            const comment: Comment[] = state.getIn(['applicationComment', 'data']);
            const newCommentData = comment.concat(data);
            return state
                .setIn(['applicationComment', 'loading'], false)
                .setIn(['applicationComment', 'data'], newCommentData);
        }

        case ACTIONS.GET_COMMENT_LIST.PENDING: {
            return state.setIn(['applicationComment', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.GET_COMMENT_LIST.SUCCESS: {
            let loadedList = state.getIn(['applicationComment', 'data']) ?? [];
            const commentList: CommentList = action.data as CommentList;
            const loadMore = commentList.length < action?.paginationData?.limit ? false : true;
            if (loadedList && action?.paginationData?.skip > 0) {
                loadedList = loadedList.concat(commentList);
            } else {
                loadedList = commentList;
            }
            return state
                .setIn(['applicationComment', 'loadMore'], loadMore)
                .setIn(['applicationComment', 'data'], loadedList);
        }
        case ACTIONS.GET_COMMENT_LIST.ERROR: {
            return state.setIn(['applicationComment', 'loading'], false);
        }
        case ACTIONS.SET_ACTIVE_TAB: {
            return state.setIn(['activeTab'], action.activeTab);
        }
        case ACTIONS.GET_INSPECTION_DETAIL.PENDING: {
            return state.setIn(['applicationInspection', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.GET_INSPECTION_DETAIL.SUCCESS: {
            return state
                .setIn(['applicationInspection', 'loading'], false)
                .setIn(['inspectionEditForm', 'data'], action.data.editForm);
        }
        case ACTIONS.GET_INSPECTION_DETAIL.ERROR: {
            return state.setIn(['applicationInspection', 'loading'], false);
        }

        case ACTIONS.GET_MERGE_TEMPLATES.PENDING: {
            return state.setIn(['templates', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.GET_MERGE_TEMPLATES.SUCCESS: {
            const { data: templates } = action;
            return state.setIn(['templates', 'loading'], false).setIn(['templates', 'data'], templates);
        }
        case ACTIONS.GET_MERGE_TEMPLATES.ERROR: {
            return state.setIn(['templates', 'loading'], false);
        }
        case ACTIONS.DOWNLOAD_DOCUMENT.PENDING: {
            return state.setIn(['templates', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.DOWNLOAD_DOCUMENT.SUCCESS: {
            return state.setIn(['templates', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.DOWNLOAD_DOCUMENT.ERROR: {
            return state.setIn(['templates', 'loading'], false);
        }

        case ACTIONS.PERSON_SEARCH.PENDING: {
            return state.setIn(['personListSearched', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.PERSON_SEARCH.SUCCESS: {
            return state
                .setIn(['personListSearched', 'loading'], false)
                .setIn(['personListSearched', 'data'], action.data);
        }
        case ACTIONS.PERSON_SEARCH.ERROR: {
            return state
                .setIn(['personListSearched', 'loading'], false)
                .setIn(['personListSearched', 'error'], action.error);
        }

        case ACTIONS.PRINT_DOCUMENTS_HISTORY_LIST.PENDING: {
            return state.setIn(['printDocumentsHistoryList', 'loading'], action.status === 'LOADING' ? true : false);
        }
        case ACTIONS.PRINT_DOCUMENTS_HISTORY_LIST.SUCCESS: {
            let loadedList = state.getIn(['printDocumentsHistoryList', 'data']) ?? [];
            const printDocumentsHistoryList = action.data;
            const loadMore = printDocumentsHistoryList.length < action.paginationData.limit ? false : true;
            if (loadedList && action?.paginationData?.skip > 0) {
                loadedList = loadedList.concat(printDocumentsHistoryList);
            } else {
                loadedList = printDocumentsHistoryList;
            }
            return state
                .setIn(['printDocumentsHistoryList', 'data'], loadedList)
                .setIn(['printDocumentsHistoryList', 'loadMore'], loadMore)
                .setIn(['printDocumentsHistoryList', 'loading'], false);
        }
        case ACTIONS.PRINT_DOCUMENTS_HISTORY_LIST.ERROR: {
            return state
                .setIn(['printDocumentsHistoryList', 'data'], [])
                .setIn(['printDocumentsHistoryList', 'loading'], false)
                .setIn(['printDocumentsHistoryList', 'error'], action.error);
        }
        case ACTIONS.SET_LICENSE_COLUMN_SORT: {
            return state
                .setIn(['licenseSort', 'orderBy'], action.payload.orderBy)
                .setIn(['licenseSort', 'orderCol'], action.payload.orderCol);
        }

        default: {
            return state;
        }
    }
}
